import {makeAutoObservable, observable, runInAction} from 'mobx'
import {Lang} from 'yougile-lang'
import {Debouncer, logger, ObjUtil, Util} from 'yougile-util'
import {translitCy} from 'yougile-util'
import {
  createDataObjectId,
  DataObjectId,
  duplicateRole,
  Hub,
  HubData,
  isDataObjectId,
  makeDefaultRole,
  PermissionCustomRole,
  PermissionRoleKey,
  SandboxTree,
  User
} from 'yougile-model'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {InputApi} from '@components/common/base/input/types'
import {
  showConfirm,
  showDeleteConfirm
} from '@components/common/base/notifications'
import {createUseStore} from '@containers/hooks'
import {checkProjectPrefix} from '@extensions/id-tasks/check-project-prefix'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {setProjectId} from '@extensions/id-tasks/set-project-id'
import {compare} from './helpers'
import {ProjectParticipantsUnionProps, Roles, Sort, Tab} from './types'

interface State {
  title: string
  filter: string
  newRoles: HubData['roles']
  chosenRoles: Roles
  editedRole?: PermissionCustomRole
  tab: Tab
  sort?: Sort
  copyRole: boolean
  allUsers: User[]
  sortAsc: boolean
  projId: string
  projPrefixInvalid?: boolean
  projPrefixError?: boolean
  projPrefixErrorText?: string
  prefixFirstChanged?: boolean
}

const initState: State = {
  title: '',
  projId: '',
  filter: '',
  newRoles: {},
  chosenRoles: {},
  tab: 'users',
  copyRole: false,
  allUsers: [],
  sortAsc: true
}

const triggerDebouncer = new Debouncer(500)

export class ProjectParticipantsStore {
  state: State = initState
  props: ProjectParticipantsUnionProps
  newProjectId: DataObjectId
  box: SandboxTree
  savedRolesRef: Roles
  invitedUsers?: string[]
  roleForNewProject: PermissionRoleKey = 'worker'

  constructor(props: ProjectParticipantsUnionProps) {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})

    this.box = Sync.getCurrentSandbox()
    this.newProjectId = createDataObjectId()

    this.props = props
    this.state.chosenRoles = this.props.roles
    this.savedRolesRef = {...this.props.roles}
    this.state.allUsers = [...this.props.allUsers]
  }

  syncProps(props: ProjectParticipantsUnionProps): void {
    this.props = props
    this.savedRolesRef = {...this.props.roles}
    this.state.allUsers = [...this.props.allUsers]

    const invitedIds = this.props.allUsers
      .map(u => {
        if (this.invitedUsers?.includes(u.email)) {
          return u.id
        }

        return undefined
      })
      .filter(Boolean)

    const obj = invitedIds.reduce(
      (acc, prev) => {
        acc[prev] = this.roleForNewProject

        return acc
      },
      {} as {[key: string]: PermissionRoleKey}
    )

    this.state.chosenRoles = {
      ...this.props.roles,
      ...this.state.chosenRoles,
      ...obj
    }
  }

  setDefaultRoleForNewProject(value: PermissionRoleKey): void {
    this.roleForNewProject = value
  }

  setTitle(api: InputApi): void {
    this.state.title = Util.removeLeadingString(api.text, '.')

    if (!this.state.projId) {
      this.state.prefixFirstChanged = false
    }

    if (!this.state.prefixFirstChanged) {
      const prefix = this.processTitleProjPrefix(this.state.title)

      if (prefix !== this.state.projId) {
        this.state.projId = prefix
        void this.checkProjPrefixError()
      }
    }
  }

  processTitleProjPrefix(text: string): string {
    if (!text) {
      return ''
    }
    const removedOtherSymbols = text.replace(/[^a-zA-Zа-яА-Я0-9]/g, '')
    const shortText = removedOtherSymbols.slice(0, 3)
    const onlyEn = translitCy(shortText)
    return onlyEn.toUpperCase().slice(0, 5)
  }

  async checkProjPrefixError(): Promise<void> {
    triggerDebouncer.runButNotOften(() => this.checkProjPrefixErrorBase())
  }

  async checkProjPrefixErrorBase(): Promise<void> {
    const text = this.state.projId
    if (!text) {
      this.state.projPrefixError = false
      this.state.projPrefixErrorText = ''
    }

    if (text === this.state.projPrefixErrorText) {
      return
    }

    const data = await checkProjectPrefix(text)

    runInAction(() => {
      this.state.projPrefixError = data
      this.state.projPrefixErrorText = text
    })
  }

  setProjId(api: InputApi): void {
    this.state.prefixFirstChanged = true

    const prefix = this.processProjPrefix(api.text)
    this.state.projId = prefix
    this.state.projPrefixInvalid = /[^a-zA-Z0-9]/g.test(prefix)
    if (!this.state.projPrefixInvalid) {
      void this.checkProjPrefixError()
    }
  }

  processProjPrefix(text: string): string {
    if (!text) {
      return ''
    }
    const removedOtherSymbols = text.replace(/[^a-zA-Zа-яА-Я0-9]/g, '')
    const shortText = removedOtherSymbols.slice(0, 5)
    return shortText.toUpperCase().slice(0, 5)
  }

  setFilter(api: InputApi): void {
    this.state.filter = api.text || ''
  }
  resetFilter(): void {
    this.state.filter = ''
  }

  setNewRoles(value: HubData['roles']): void {
    this.state.newRoles = value
  }

  setChosenRoles(value: Roles): void {
    this.state.chosenRoles = value
  }

  updateInvited(emails: string[]): void {
    this.invitedUsers = emails
  }

  submit(): void {
    if (!this.hasChanges) {
      return
    }

    if (!this.props.project && !this.state.title) {
      return
    }

    const id = this.props.project?.id ?? this.newProjectId

    this.props.save(
      id,
      this.props.project?.title ?? this.state.title,
      this.state.chosenRoles,
      this.props.project ? undefined : this.state.newRoles
    )

    void setProjectId(id, this.state.projId)
    idTasksApi.setIdsProject(id, this.state.projId)
  }

  get hasChanges(): boolean {
    if (this.state.projPrefixError || this.state.projPrefixInvalid) {
      return false
    }

    if (this.isCreateProject && !this.state.projId.length) {
      return false
    }

    if (this.isCreateProject) {
      return !!this.state.title
    }

    return !ObjUtil.deepEqual(this.props.roles, this.state.chosenRoles, true)
  }

  setEditedRole(value: PermissionCustomRole): void {
    this.state.editedRole = value
  }

  private setTab(value: Tab): void {
    this.state.tab = value
  }
  selectUsersTab(): void {
    this.setTab('users')
  }
  selectRolesTab(): void {
    this.setTab('roles')
  }
  selectInviteTab(): void {
    this.setTab('invite')
  }

  setSort(value: Sort): void {
    this.state.sort = value
  }
  setCopyRole(value: boolean): void {
    this.state.copyRole = value
  }
  setCopyRoleOn(): void {
    this.setCopyRole(true)
  }
  setCopyRoleOff(): void {
    this.setCopyRole(false)
  }
  setAllUsers(value: User[]): void {
    this.state.allUsers = value
  }
  setSortAsc(value: boolean): void {
    this.state.sortAsc = value
  }
  toggleSortAsc(): void {
    this.state.sortAsc = !this.state.sortAsc
  }
  get rolesSupported(): boolean {
    // TODO: remove
    return Backend.features.customRoles
  }

  createRole(): void {
    if (!this.rolesSupported) {
      return
    }
    this.setTab('roles')
    this.setEditedRole(makeDefaultRole())
  }

  sortUsers(): void {
    const users = [...this.state.allUsers]

    this.state.allUsers = users.sort(
      (a, b) =>
        compare(
          this.state.sort,
          this.state.sortAsc,
          this.state.chosenRoles,
          this.box
        )(a, b) || compare('name', true, this.state.chosenRoles, this.box)(a, b)
    )
  }

  getCustomRoles(): PermissionCustomRole[] {
    return ObjUtil.list(
      {...(this.props.project?.data?.roles ?? {}), ...this.state.newRoles},
      (_id, v) => v
    ).reverse()
  }

  async deleteRole(r: PermissionCustomRole): Promise<void> {
    const project = this.props.project
    if (!project) {
      return
    }
    const warn = Lang.msg.proj.deleteRoleWarn1
    if (await showDeleteConfirm(warn)) {
      this.box.getAllUsers(false)
      delete project.data.roles[r.key]
      ObjUtil.forEach(project.data.userPermissions, (uid, k) => {
        if (k === r.key) {
          delete project.data.userPermissions[uid]
        }
      })
      const chosen = {...this.state.chosenRoles}
      ObjUtil.forEach(chosen, (uid, k) => {
        if (k === r.key) {
          delete chosen[uid]
        }
      })
      const newRoles2 = {...this.state.newRoles}
      ObjUtil.forEach(newRoles2, (k: PermissionRoleKey) => {
        if (k === r.key) {
          delete newRoles2[k]
        }
      })
      this.setChosenRoles(chosen)
      this.setNewRoles(newRoles2)
      project.commit()
    }
  }

  cancelEditRole(): void {
    this.setEditedRole(undefined)
  }

  saveRole(role: PermissionCustomRole): void {
    if (!this.rolesSupported) {
      return
    }
    const project = this.props.project

    const {key} = this.state.editedRole ?? role
    if (isDataObjectId(key)) {
      if (!role) {
        if (project) {
          project.data.roles = project.data.roles ?? {}
          delete project.data.roles[key]
          project.commit()
        } else {
          const {[key]: _, ...r} = this.state.newRoles
          this.setNewRoles({...r})
        }
      } else if (role.key === key) {
        if (project) {
          project.data.roles = {
            ...(project.data.roles ?? {}),
            [role.key]: role
          }
          project.commit()
        } else {
          this.setNewRoles({...this.state.newRoles, [key]: role})
        }
      } else {
        logger.log(`Can‘t modify role id`)
      }
    } else {
      logger.log(`Can’t modify system role ${key}`)
    }
    this.cancelEditRole()
  }

  onCopyRole(_project: Hub[], role: PermissionCustomRole): void {
    this.setCopyRole(false)
    const newRole = duplicateRole(role)

    // NOTE: When creating a new role by copying an existing one, we remove
    // `newRole.permissions.children` because the target project does not have
    // the same boards and columns. Therefore, we don't need to copy settings
    // related to the boards and columns of the original project.
    delete newRole.permissions.children

    this.saveRole(newRole)
  }

  async closeWithWarn(): Promise<void> {
    if (this.state.tab === 'invite') {
      this.selectUsersTab()
      return
    }

    const noChanges =
      !this.hasChanges && Object.keys(this.state.newRoles).length === 0
    if (noChanges || (await showConfirm(Lang.msg.proj.unsavedChangesHint))) {
      this.close()
    }
  }

  close(): void {
    if (this.state.tab === 'invite') {
      this.selectUsersTab()
    } else {
      this.props.close()
    }
  }

  get isUsersTab(): boolean {
    return this.state.tab === 'users' || !this.rolesSupported
  }

  get isRolesTab(): boolean {
    return this.state.tab === 'roles' && this.rolesSupported
  }

  get isInviteTab(): boolean {
    return this.state.tab === 'invite'
  }

  get isCreateProject(): boolean {
    return !this.props.project
  }

  get popupTitle(): string {
    if (this.isCreateProject) {
      if (this.props.type === 'crm') {
        return Lang.msg.crm.newProjectTitle
      }

      return Lang.msg.common.newProjectTitle
    }

    const p = this.props.project

    return Lang.msg.reg.projectParticipants + ' “' + p.title + '”'
  }
}

const [ProjectParticipantContext, useProjectParticipantStore] = createUseStore(
  ProjectParticipantsStore
)
export {ProjectParticipantContext, useProjectParticipantStore}
